import { useCallback, useEffect, useState } from "react";
import { api, errMsg, download, MONTHS_IT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderOpen, Folder, FileText, CaretRight, CaretDown, ArrowsClockwise, Archive as ArchiveIcon, Lock, UploadSimple, Eye, Printer, WhatsappLogo, MagnifyingGlass, Trash, Plus } from "@phosphor-icons/react";

const Files = ({ files, onShare, onDelete }) => (
  <ul className="pl-7 pb-1">
    {files.map((f) => (
      <li key={f.id} className="flex items-center gap-1.5">
        <button className="flex flex-1 items-center gap-2 py-1 text-sm hover:text-primary transition-colors"
          data-testid={`archive-file-${f.id}`}
          onClick={() => download(`/archive/file/${f.id}`, f.name).catch((e) => toast.error(errMsg(e)))}>
          <FileText size={15} className="text-muted-foreground" /> {f.name}
        </button>
        <button title="Condividi su WhatsApp" data-testid={`archive-share-${f.id}`}
          className="shrink-0 rounded-sm p-1 text-emerald-600 transition-colors hover:bg-emerald-50"
          onClick={() => onShare(f)}>
          <WhatsappLogo size={16} />
        </button>
        {onDelete && (
          <button title="Elimina PDF" data-testid={`archive-delete-${f.id}`}
            className="shrink-0 rounded-sm p-1 text-rose-600 transition-colors hover:bg-rose-50"
            onClick={() => onDelete(f)}>
            <Trash size={16} />
          </button>
        )}
      </li>
    ))}
    {files.length === 0 && <li className="py-1 text-sm text-muted-foreground">Cartella vuota</li>}
  </ul>
);

const FolderNode = ({ folder, files, open, onToggle, onShare, onDelete }) => (
  <div className="border-b border-border last:border-0" data-testid={`archive-folder-${folder}`}>
    <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40"
      onClick={onToggle} data-testid={`archive-folder-toggle-${folder}`}>
      {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
      {open ? <FolderOpen size={17} className="text-primary" /> : <Folder size={17} className="text-primary" />}
      <span className="font-semibold text-sm">{folder}</span>
      <span className="ml-auto text-xs text-muted-foreground tabular-nums">{files.length} file</span>
    </button>
    {open && <Files files={files} onShare={onShare} onDelete={onDelete} />}
  </div>
);

const DAY_STATE = {
  ok: { cls: "bg-emerald-500 text-white border-emerald-600", label: "Completo e archiviato" },
  "da-archiviare": { cls: "bg-amber-400 text-amber-950 border-amber-500", label: "Pronto, non ancora archiviato" },
  manca: { cls: "bg-red-500 text-white border-red-600", label: "Mancano pesi o divisione cottimo" },
  vuoto: { cls: "bg-muted text-muted-foreground border-border", label: "Nessun dato" },
};

const MonthLights = ({ status, onOpenDay }) => (
  <div className="rounded-sm border border-border bg-card p-3" data-testid="archive-lights">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
      <span className="font-head font-semibold text-sm">Stato giornate — {status.label}</span>
      {Object.entries(DAY_STATE).map(([k, v]) => (
        <span key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className={`h-2.5 w-2.5 rounded-full border ${v.cls}`} /> {v.label}
        </span>
      ))}
    </div>
    <div className="flex flex-wrap gap-1">
      {status.days.map((d) => {
        const s = DAY_STATE[d.state] || DAY_STATE.vuoto;
        const detail = [
          d.has_weights ? "pesi registrati" : "nessuna pesata",
          d.split_pending.length ? `divisione cottimo mancante: ${d.split_pending.join(", ")}` : "divisione cottimo ok",
          d.attendance ? "presenze registrate" : "presenze mancanti",
          d.archived_folders.length ? `archiviati: ${d.archived_folders.length}` : "non archiviato",
        ].join(" · ");
        return (
          <button key={d.day} type="button" onClick={() => onOpenDay(d)}
            title={`${d.n} — ${s.label}\n${detail}${d.state === "manca" ? "\nClicca per sistemare la giornata" : ""}`}
            data-testid={`day-light-${d.day}`}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-sm border text-xs font-semibold tabular-nums transition-transform hover:scale-110 ${s.cls}`}>
            {d.n}
          </button>
        );
      })}
    </div>
  </div>
);

export default function Archive() {
  const { user } = useAuth();
  const nav = useNavigate();
  const now = new Date();
  const [tree, setTree] = useState(null);
  const [open, setOpen] = useState({});
  const [openMonth, setOpenMonth] = useState({});
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [busy, setBusy] = useState("");
  const [perms, setPerms] = useState(null);
  const [aggiungi, setAggiungi] = useState(null);
  const [upload, setUpload] = useState(null);
  const [status, setStatus] = useState(null);
  const [mancanti, setMancanti] = useState(null);
  const [apriMancanti, setApriMancanti] = useState(false);
  const [closing, setClosing] = useState(null);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState([]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) { setFound([]); return; }
    const t = setTimeout(() => {
      api.get("/archive/search", { params: { q: term } })
        .then((r) => setFound(r.data.results)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadStatus = useCallback(async () => {
    try { setStatus((await api.get(`/archive/month-status?year=${year}&month=${month}`)).data); }
    catch { setStatus(null); }
    try { setMancanti((await api.get(`/archive/missing-causali?year=${year}&month=${month}`)).data); }
    catch { setMancanti(null); }
  }, [year, month]);

  const openDay = (d) => {
    if (d.split_pending.length) {
      nav(`/pesi/divisione?data=${d.day}&linea=${d.split_pending[0]}`);
    } else if (!d.has_weights) {
      nav(`/pesi/registro?data=${d.day}`);
    } else if (!d.attendance) {
      nav(`/presenze?data=${d.day}`);
    } else if (d.state === "da-archiviare") {
      archiveDay(d.day);
    }
  };

  const archiveDay = async (day) => {
    setBusy("auto");
    try {
      const r = await api.post("/archive/auto", { day, notify: false });
      toast.success(r.data.archived.length
        ? `Archiviato il ${new Date(day).toLocaleDateString("it-IT")}`
        : "Giornata non ancora completa");
      load();
      loadStatus();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const load = useCallback(async () => {
    try { setTree((await api.get("/archive/tree")).data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canManage = ["admin", "manager"].includes(user?.role);
  useEffect(() => { if (canManage) loadStatus(); }, [canManage, loadStatus]);

  // il semaforo si aggiorna appena si torna sulla pagina (es. dopo aver salvato la divisione cottimo)
  useEffect(() => {
    if (!canManage) return;
    const refresh = () => { if (document.visibilityState === "visible") { loadStatus(); load(); } };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [canManage, loadStatus, load]);

  const shareFile = async (f) => {
    try {
      const r = await api.post(`/archive/share/${f.id}`);
      window.open(r.data.whatsapp, "_blank", "noopener");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Eliminare "${f.name}" dall'archivio?`)) return;
    try {
      await api.delete(`/archive/file/${f.id}`);
      toast.success("PDF eliminato");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openFile = (d) => download(`/archive/file/${d.id}`, d.name).catch((e) => toast.error(errMsg(e)));

  const printFile = async (d) => {
    try {
      const r = await api.get(`/archive/file/${d.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const f = document.createElement("iframe");
      f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
      f.src = url;
      f.onload = () => { f.contentWindow.focus(); f.contentWindow.print(); };
      document.body.appendChild(f);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startClose = async () => {
    setBusy("close");
    try {
      const p = await api.get(`/archive/close-month/preview?year=${year}&month=${month}`);
      setClosing({ ...p.data, step: p.data.conflicts.length ? "conflict" : "confirm" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const doClose = async (mode) => {
    setBusy("close");
    try {
      const r = await api.post("/archive/close-month", { year, month, mode });
      setClosing({ ...(closing || {}), step: "done", label: r.data.label,
                   created: r.data.created || [], total: r.data.documents });
      toast.success(`${(r.data.created || []).length} PDF generati in ${r.data.label}`);
      load();
      loadStatus();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const run = async (path, label) => {
    setBusy(label);
    try {
      if (label === "sync") {
        let created = 0, guard = 0;
        for (;;) {
          const r = await api.post(path, { year, month, limit: 4 });
          created += r.data.created;
          setBusy(`sync:${created}/${r.data.total_days}`);
          if (!r.data.remaining || ++guard > 40) break;
        }
        toast.success(`${created} PDF archiviati`);
      } else {
        const r = await api.post(path, { year, month });
        toast.success(`Mese chiuso: ${r.data.documents} documenti in ${r.data.label}`);
      }
      load();
      loadStatus();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const regenerate = async () => {
    setBusy("regen");
    try {
      let total = 0, done = [], guard = 0;
      for (;;) {
        const r = await api.post("/archive/regenerate", {
          date_from: `${year}-${String(month).padStart(2, "0")}-01`,
          date_to: new Date().toISOString().slice(0, 10), limit: 6, done });
        total += r.data.regenerated;
        done = r.data.done;
        setBusy(`regen:${done.length}/${r.data.total_days}`);
        if (!r.data.remaining || ++guard > 40) break;
      }
      toast.success(`${total} PDF rigenerati con le divisioni cottimo aggiornate`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const openPerms = async () => {
    try { setPerms((await api.get("/archive/permissions")).data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doUpload = async () => {
    const fd = new FormData();
    fd.append("folder", upload.folder);
    fd.append("month", `${year}-${String(month).padStart(2, "0")}`);
    upload.files.forEach((f) => fd.append("files", f));
    setBusy("upload");
    try {
      const r = await api.post("/archive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.uploaded.length} PDF caricati in ${r.data.folder}`);
      setUpload(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const savePerm = async (folder, ids) => {
    try {
      const r = await api.put("/archive/permissions", { folder, employee_ids: ids });
      setPerms(r.data);
      toast.success("Permessi aggiornati");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="archive-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Archivio Documenti</h1>
          <p className="text-sm text-muted-foreground mt-1">
            PDF giornalieri del mese in corso e archivio storico per mese. Clicca un file per aprirlo.
          </p>
        </div>
        {canManage && (
          <div className="mobile-stack flex flex-wrap gap-2 items-center">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="archive-month"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-[105px] rounded-sm" data-testid="archive-year"><SelectValue /></SelectTrigger>
              <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!!busy}
              data-testid="archive-sync" onClick={() => run("/archive/sync", "sync")}>
              <ArrowsClockwise size={14} className="mr-1.5" />
              {busy.startsWith("sync") ? `Archiviazione ${busy.split(":")[1] || ""}...` : "ARCHIVIA I PDF DEL MESE"}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" disabled={!!busy}
              data-testid="archive-regenerate" onClick={regenerate}
              title="Sostituisce i PDF già in archivio con le divisioni cottimo attuali">
              <ArrowsClockwise size={14} className="mr-1.5" />
              {busy.startsWith("regen") ? `Rigenerazione ${busy.split(":")[1] || ""}...` : "RIGENERA I PDF"}
            </Button>
            <Button size="sm" className="rounded-sm font-semibold" disabled={!!busy}
              data-testid="archive-close" onClick={startClose}>
              <ArchiveIcon size={14} className="mr-1.5" />
              {busy === "close" ? "Chiusura..." : "CHIUDI IL MESE"}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="archive-upload-open"
              onClick={() => setUpload({ folder: tree?.folders?.[0] || "PESI BOVINO", files: [] })}>
              <UploadSimple size={14} className="mr-1.5" /> CARICA PDF
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" data-testid="archive-perms" onClick={openPerms}>
              <Lock size={14} className="mr-1.5" /> PERMESSI
            </Button>
          </div>
        )}
      </div>

      {canManage && status && <MonthLights status={status} onOpenDay={openDay} />}

      {canManage && mancanti && (
        <div className="rounded-sm border border-border bg-card" data-testid="archive-missing-box">
          <button type="button" data-testid="archive-missing-toggle"
            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40"
            onClick={() => setApriMancanti(!apriMancanti)}>
            {apriMancanti ? <CaretDown size={14} /> : <CaretRight size={14} />}
            <span className="font-head font-semibold text-sm">
              Giorni con causali mancanti — {mancanti.label}
            </span>
            <span className={`ml-auto rounded-sm px-2 py-0.5 text-xs font-bold ${mancanti.days.length
              ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300"
              : "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"}`}>
              {mancanti.days.length ? `${mancanti.days.length} giorni incompleti` : "tutti completi"}
            </span>
          </button>
          {apriMancanti && (
            <div className="border-t border-border" data-testid="archive-missing-list">
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Il PDF presenze del giorno viene creato solo quando tutti i {mancanti.employees} dipendenti
                attivi hanno una causale. Clicca un giorno per completarlo.
              </p>
              {mancanti.days.map((g) => (
                <button key={g.day} type="button" data-testid={`archive-missing-${g.day}`}
                  className="flex w-full flex-wrap items-center gap-2 border-t border-border px-3 py-2 text-left hover:bg-secondary/40"
                  onClick={() => nav(`/presenze?data=${g.day}`)}>
                  <span className="font-semibold text-sm tabular-nums">
                    {new Date(g.day).toLocaleDateString("it-IT")}
                  </span>
                  <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                    {g.missing} su {g.total} senza causale
                  </span>
                  <span className="w-full truncate text-xs text-muted-foreground" title={g.names.join(", ")}>
                    {g.names.join(", ")}
                  </span>
                </button>
              ))}
              {mancanti.days.length === 0 && (
                <p className="border-t border-border px-3 py-6 text-center text-sm text-muted-foreground"
                  data-testid="archive-missing-empty">
                  Tutte le giornate del mese hanno le causali complete
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {tree && (
        <div className="rounded-sm border border-border bg-card p-3" data-testid="archive-search-box">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca un PDF per nome in tutto l'archivio (min. 2 caratteri)..."
              className="h-9 rounded-sm pl-8" data-testid="archive-search-input" />
          </div>
          {search.trim().length >= 2 && (
            <div className="mt-2 max-h-72 overflow-y-auto scroll-thin rounded-sm border border-border"
              data-testid="archive-search-results">
              {found.map((f) => (
                <div key={f.id} className="flex items-center gap-2 border-b border-border px-3 py-1.5 last:border-0"
                  data-testid={`archive-search-row-${f.id}`}>
                  <button className="flex flex-1 items-center gap-2 text-left text-sm transition-colors hover:text-primary"
                    data-testid={`archive-search-file-${f.id}`}
                    onClick={() => download(`/archive/file/${f.id}`, f.name).catch((e) => toast.error(errMsg(e)))}>
                    <FileText size={15} className="shrink-0 text-muted-foreground" />
                    <span className="truncate">{f.name}</span>
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">{f.label} · {f.folder}</span>
                  </button>
                  <button title="Condividi su WhatsApp" data-testid={`archive-search-share-${f.id}`}
                    className="shrink-0 rounded-sm p-1 text-emerald-600 transition-colors hover:bg-emerald-50"
                    onClick={() => shareFile(f)}>
                    <WhatsappLogo size={16} />
                  </button>
                  {canManage && (
                    <button title="Elimina PDF" data-testid={`archive-delete-${f.id}`}
                      className="shrink-0 rounded-sm p-1 text-rose-600 transition-colors hover:bg-rose-50"
                      onClick={async () => { await deleteFile(f); setFound(found.filter((x) => x.id !== f.id)); }}>
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              ))}
              {found.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="archive-search-empty">
                  Nessun PDF trovato con questo nome
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {tree && (
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="rounded-sm border border-border bg-card" data-testid="archive-current">
            <div className="px-3 py-2 border-b border-border bg-secondary/60">
              <span className="font-head font-semibold">Cartella corrente — {tree.current.label}</span>
            </div>
            {tree.current.folders.map((f) => (
              <FolderNode key={f.folder} folder={f.folder} files={f.files} onShare={shareFile}
                onDelete={canManage ? deleteFile : null}
                open={!!open[f.folder]} onToggle={() => setOpen({ ...open, [f.folder]: !open[f.folder] })} />
            ))}
            {tree.current.folders.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="archive-current-empty">
                Nessun documento per il mese in corso: usa "ARCHIVIA I PDF DEL MESE"
              </p>
            )}
          </div>

          <div className="rounded-sm border border-border bg-card" data-testid="archive-old">
            <div className="px-3 py-2 border-b border-border bg-secondary/60">
              <span className="font-head font-semibold">Archivio</span>
            </div>
            {tree.archive.map((m) => (
              <div key={m.month} className="border-b border-border last:border-0">
                <button className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/40"
                  data-testid={`archive-month-${m.month}`}
                  onClick={() => setOpenMonth({ ...openMonth, [m.month]: !openMonth[m.month] })}>
                  {openMonth[m.month] ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  <ArchiveIcon size={17} className="text-amber-600" />
                  <span className="font-semibold text-sm">{m.label}</span>
                </button>
                {openMonth[m.month] && (
                  <div className="pl-5">
                    {m.folders.map((f) => (
                      <FolderNode key={f.folder} folder={f.folder} files={f.files} onShare={shareFile}
                        onDelete={canManage ? deleteFile : null}
                        open={!!open[m.month + f.folder]}
                        onToggle={() => setOpen({ ...open, [m.month + f.folder]: !open[m.month + f.folder] })} />
                    ))}
                  </div>
                )}
              </div>
            ))}
            {tree.archive.length === 0 && (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="archive-old-empty">
                Nessun mese archiviato
              </p>
            )}
          </div>
        </div>
      )}

      {perms && (
        <Dialog open onOpenChange={() => setPerms(null)}>
          <DialogContent className="rounded-sm sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="perms-dialog">
            <DialogHeader><DialogTitle className="font-head">Permessi cartelle</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              Per ogni cartella sono elencati solo i dipendenti già abilitati a vederla dalla loro area personale.
            </p>
            <div className="space-y-3">
              {perms.folders.map((f) => {
                const abilitati = perms.employees.filter((e) => f.employee_ids.includes(e.id));
                const disponibili = perms.employees.filter((e) => !f.employee_ids.includes(e.id));
                return (
                  <div key={f.folder} className="rounded-sm border border-border p-3" data-testid={`perm-${f.folder}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="font-semibold text-sm">{f.folder}
                        <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                          {abilitati.length} abilitati
                        </span>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 rounded-sm font-semibold"
                        data-testid={`perm-add-btn-${f.folder}`}
                        onClick={() => setAggiungi({ folder: f.folder, ids: f.employee_ids, disponibili })}>
                        <Plus size={14} className="mr-1.5" /> Aggiungi dipendente
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-1 max-h-40 overflow-y-auto scroll-thin">
                      {abilitati.map((e) => (
                        <div key={e.id} className="flex items-center justify-between gap-2 text-sm rounded-sm px-2 py-1 bg-secondary/50"
                          data-testid={`perm-${f.folder}-${e.id}`}>
                          <span className="truncate">{e.name}</span>
                          <button type="button" title="Rimuovi dalla cartella"
                            data-testid={`perm-remove-${f.folder}-${e.id}`}
                            className="shrink-0 rounded-sm p-1 text-rose-600 hover:text-rose-700"
                            onClick={() => savePerm(f.folder, f.employee_ids.filter((x) => x !== e.id))}>
                            <Trash size={15} />
                          </button>
                        </div>
                      ))}
                      {!abilitati.length && (
                        <div className="text-sm text-muted-foreground" data-testid={`perm-empty-${f.folder}`}>
                          Nessun dipendente abilitato
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button className="rounded-sm" onClick={() => setPerms(null)} data-testid="perms-close">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {aggiungi && (
        <Dialog open onOpenChange={() => setAggiungi(null)}>
          <DialogContent className="rounded-sm sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="perm-add-dialog">
            <DialogHeader><DialogTitle className="font-head">Aggiungi a {aggiungi.folder}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Dipendenti non ancora abilitati a questa cartella.</p>
            <div className="divide-y divide-border">
              {aggiungi.disponibili.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                  <span className="truncate">{e.name}</span>
                  <Button size="sm" variant="outline" className="h-8 rounded-sm"
                    data-testid={`perm-add-${aggiungi.folder}-${e.id}`}
                    onClick={async () => {
                      await savePerm(aggiungi.folder, [...aggiungi.ids, e.id]);
                      setAggiungi({ ...aggiungi, ids: [...aggiungi.ids, e.id],
                        disponibili: aggiungi.disponibili.filter((x) => x.id !== e.id) });
                    }}>
                    <Plus size={14} className="mr-1.5" /> Aggiungi
                  </Button>
                </div>
              ))}
              {!aggiungi.disponibili.length && (
                <div className="py-4 text-sm text-muted-foreground" data-testid="perm-add-empty">
                  Tutti i dipendenti sono già abilitati a questa cartella.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button className="rounded-sm" onClick={() => setAggiungi(null)} data-testid="perm-add-close">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {upload && (
        <Dialog open onOpenChange={() => setUpload(null)}>
          <DialogContent className="rounded-sm sm:max-w-lg" data-testid="upload-dialog">
            <DialogHeader><DialogTitle className="font-head">Carica PDF nell'archivio</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              I file finiscono in <b>{MONTHS_IT[month - 1]} {year}</b> nella cartella scelta.
              Cambia mese e anno in alto per archiviare in un altro periodo.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">Cartella</label>
                <Select value={upload.folder} onValueChange={(v) => setUpload({ ...upload, folder: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="upload-folder"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(tree?.folders || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground">File PDF</label>
                <input type="file" accept="application/pdf" multiple data-testid="upload-files"
                  className="mt-1 block w-full text-sm rounded-sm border border-border p-2"
                  onChange={(e) => setUpload({ ...upload, files: Array.from(e.target.files || []) })} />
                {upload.files.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1" data-testid="upload-count">
                    {upload.files.length} file selezionati
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setUpload(null)}
                data-testid="upload-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" disabled={!upload.files.length || busy === "upload"}
                onClick={doUpload} data-testid="upload-submit">
                {busy === "upload" ? "Caricamento..." : "CARICA"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {closing && (
        <Dialog open onOpenChange={() => setClosing(null)}>
          <DialogContent className="rounded-sm sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="close-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">
                {closing.step === "done" ? `PDF generati — ${closing.label}` : `Chiusura di ${closing.label}`}
              </DialogTitle>
            </DialogHeader>

            {closing.step === "confirm" && (
              <>
                <p className="text-sm text-muted-foreground">
                  Verranno generati <b>{closing.documents.length} PDF</b> e salvati in
                  Archivio → {closing.label} → CHIUSURA MENSILE.
                </p>
                <ul className="max-h-64 space-y-1 overflow-y-auto rounded-sm border border-border p-3 text-sm"
                  data-testid="close-preview-list">
                  {closing.documents.map((n) => (
                    <li key={n} className="flex items-center gap-2">
                      <FileText size={14} className="text-muted-foreground" /> {n}
                    </li>
                  ))}
                </ul>
                <DialogFooter>
                  <Button variant="outline" className="rounded-sm" onClick={() => setClosing(null)}
                    data-testid="close-cancel">Annulla</Button>
                  <Button className="rounded-sm font-semibold" disabled={busy === "close"}
                    onClick={() => doClose("overwrite")} data-testid="close-start">
                    {busy === "close" ? "Genero i PDF..." : "GENERA E ARCHIVIA"}
                  </Button>
                </DialogFooter>
              </>
            )}

            {closing.step === "conflict" && (
              <>
                <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="close-conflict">
                  In archivio ci sono già <b>{closing.conflicts.length} file</b> con lo stesso nome
                  per {closing.label}. Vuoi sovrascriverli o tenere entrambe le versioni?
                </div>
                <ul className="max-h-56 space-y-1 overflow-y-auto rounded-sm border border-border p-3 text-sm">
                  {closing.conflicts.map((n) => (
                    <li key={n} className="flex items-center gap-2">
                      <FileText size={14} className="text-amber-600" /> {n}
                    </li>
                  ))}
                </ul>
                <DialogFooter className="flex-col gap-2 sm:flex-row">
                  <Button variant="outline" className="rounded-sm" onClick={() => setClosing(null)}
                    data-testid="close-cancel">Annulla</Button>
                  <Button variant="outline" className="rounded-sm font-semibold" disabled={busy === "close"}
                    onClick={() => doClose("keep")} data-testid="close-keep-both">
                    TIENI ENTRAMBI
                  </Button>
                  <Button className="rounded-sm font-semibold" disabled={busy === "close"}
                    onClick={() => doClose("overwrite")} data-testid="close-overwrite">
                    SOVRASCRIVI
                  </Button>
                </DialogFooter>
              </>
            )}

            {closing.step === "done" && (
              <>
                <p className="text-sm text-muted-foreground">
                  {closing.created.length} PDF salvati in Archivio → {closing.label} → CHIUSURA MENSILE.
                  Aprili per vederli o stamparli.
                </p>
                <div className="rounded-sm border border-border" data-testid="close-result-list">
                  {closing.created.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 border-b border-border px-3 py-2 last:border-0">
                      <FileText size={15} className="shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate text-sm">{d.name}</span>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                        data-testid={`close-open-${d.id}`}
                        onClick={() => openFile(d)}>
                        <Eye size={13} className="mr-1" /> Apri
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                        data-testid={`close-print-${d.id}`}
                        onClick={() => printFile(d)}>
                        <Printer size={13} className="mr-1" /> Stampa
                      </Button>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button className="rounded-sm" onClick={() => setClosing(null)} data-testid="close-done">
                    Chiudi
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
